import { useCallback, useEffect, useState } from "react";
import { Button, Dialog } from "@hybrid-iptv/ui";
import { autostartState, openAutostartPermission, setAutostart, type AutostartState } from "./autostart.js";

/**
 * Settings -> Home: "Open when the box starts" (owner, 2026-10-12). Off by default, shown only in
 * SliqueTV's direct-download build. After a real restart or a power cut the box opens the app by
 * itself; with "Start on: Live TV" that is straight to the last channel.
 *
 * Android 10+ asks the viewer for "Display over other apps" first, so ticking the row without it
 * opens a short explanation and then Android's own screen. Coming back, the row says whether the
 * switch was given.
 */
export function AutostartRow() {
  const [state, setState] = useState<AutostartState | null>(null);
  const [asking, setAsking] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const refresh = useCallback(() => {
    void autostartState().then(setState);
  }, []);

  useEffect(() => {
    refresh();
    // Back from Android's screen: read the switch again.
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  if (!state || !state.supported) return null;

  const needsPermission = state.enabled && !state.permitted;

  const onToggle = (checked: boolean) => {
    if (!checked) {
      void setAutostart(false).then((next) => next && setState(next));
      return;
    }
    if (!state.permitted) {
      setAsking(true);
      return;
    }
    void setAutostart(true).then((next) => next && setState(next));
  };

  const openAndroid = async () => {
    setAsking(false);
    const next = await setAutostart(true);
    if (next) setState(next);
    const result = await openAutostartPermission();
    if (result === "unavailable") {
      const off = await setAutostart(false);
      if (off) setState(off);
      setUnavailable(true);
    } else {
      setUnavailable(false);
    }
  };

  return (
    <>
      <div className="settings-row">
        <label className="settings-row-label" htmlFor="autostart">
          Open when the box starts
        </label>
        <input
          id="autostart"
          type="checkbox"
          checked={state.enabled}
          onChange={(event) => onToggle(event.target.checked)}
          aria-label="Open the app by itself after the box restarts"
        />
      </div>
      <p className="settings-row-hint">
        SliqueTV opens by itself after the box restarts or loses power. With Start on: Live TV, it goes straight to
        your last channel. (Most boxes only go to standby when the TV turns off; this does not act on that.)
      </p>
      {needsPermission && (
        <p className="settings-row-hint">
          <button type="button" className="ui-btn ui-btn--ghost" onClick={() => setAsking(true)}>
            Needs Android's "Display over other apps" — select to fix
          </button>
        </p>
      )}
      {unavailable && <p className="settings-row-hint">This box doesn't let apps open by themselves.</p>}
      <Dialog
        open={asking}
        title="One Android setting"
        onClose={() => setAsking(false)}
        actions={
          <>
            <Button variant="primary" onClick={() => void openAndroid()}>
              Open Android settings
            </Button>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </>
        }
      >
        Android needs your OK before SliqueTV can open by itself. On the next screen, find SliqueTV and turn on
        "Display over other apps", then press Back.
      </Dialog>
    </>
  );
}
